import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Users, Calendar, CalendarRange, MapPin, Phone, Play } from 'lucide-react';
import EventCard from '../components/EventCard';
import VideoModal from '../components/VideoModal';
import usePublicEvents from '../hooks/usePublicEvents';
import useYouTubeVideos from '../hooks/useYouTubeVideos';

const HomeEventsEmptyState = () => (
  <div className="overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.04]">
    <div className="grid gap-0 lg:grid-cols-[0.95fr_1.05fr]">
      <div className="relative overflow-hidden bg-gradient-to-br from-[#04144c] via-[#082b57] to-[#17E6B3]/25 p-8 md:p-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.08),transparent_30%),radial-gradient(circle_at_bottom_right,rgba(23,230,179,0.2),transparent_32%)]" />
        <div className="relative">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold text-[#9CF7DF] backdrop-blur-sm">
            <CalendarRange size={16} />
            Agenda em atualizacao
          </span>
          <h3 className="mt-6 max-w-md text-3xl font-bold text-white md:text-4xl">
            Nenhum evento especial publicado agora.
          </h3>
          <p className="mt-4 max-w-lg text-base leading-7 text-[#D9E4FF]">
            Quando houver eventos com inscrição aberta, eles aparecerão aqui. Acompanhe também a página completa de eventos.
          </p>
        </div>
      </div>

      <div className="flex flex-col justify-center bg-white px-6 py-8 text-[#030B2E] md:px-10 md:py-10">
        <h4 className="text-2xl font-bold">Enquanto isso, acompanhe:</h4>
        <div className="mt-6 space-y-4">
          <div className="rounded-2xl border border-slate-200 px-5 py-4">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Programacao regular</p>
            <p className="mt-2 font-semibold">Cultos, encontros e atividades fixas da semana.</p>
          </div>
          <div className="rounded-2xl border border-slate-200 px-5 py-4">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Contato da igreja</p>
            <p className="mt-2 font-semibold">Informacoes sobre proximas inscricoes e eventos especiais.</p>
          </div>
        </div>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <Link
            to="/eventos"
            className="inline-flex items-center justify-center rounded-2xl bg-[#17E6B3] px-6 py-4 font-semibold text-[#030B2E] transition-colors hover:bg-[#0ed3a3]"
          >
            Ver pagina de eventos
          </Link>
          <Link
            to="/contato"
            className="inline-flex items-center justify-center rounded-2xl border border-slate-300 px-6 py-4 font-semibold text-slate-700 transition-colors hover:border-[#17E6B3] hover:text-[#030B2E]"
          >
            Falar com a igreja
          </Link>
        </div>
      </div>
    </div>
  </div>
);

const Home = () => {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const { videos, loading } = useYouTubeVideos(6);
  const {
    events: publicEvents,
    loading: loadingEvents,
    error: eventsError
  } = usePublicEvents({ limit: 3 });
  const heroImages = [
    '/carrocel1.jpg',
    '/carrocel2.jpg',
    '/carrocel3.jpg',
    '/carrocel4.jpg',
    '/carrocel5.jpg',
    '/carrocel6.jpg',
    '/carrocel7.jpg',
    '/carrocel8.jpg',
  ];

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCurrentSlide((previousSlide) => (previousSlide + 1) % heroImages.length);
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, [heroImages.length]);

  const eventos = [
    {
      titulo: 'Culto de Celebração',
      data: 'Domingos',
      horario: '19:00',
      descricao: 'Nossos domingos de celebração tem sido incríveis. Venha ter uma experiência única com Jesus! Estamos também no online através do canal MEVAM Brasília no YouTube. Um momento especial dedicado às famílias, com ensinamentos práticos sobre relacionamentos e princípios bíblicos.',
    },
    {
      titulo: 'Culto de Profético',
      data: 'Quinta-feiras',
      horario: '19:30',
      descricao: 'Nosso culto profético é um momento especial de busca e renovação espiritual. Venha receber uma palavra profética para sua vida!',
    },
    {
      titulo: 'PG Familiar | MEVAM Academy',
      data: 'Terças-feiras',
      horario: '20:00',
      descricao: 'Estudo sistemático da Palavra de Deus.',
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-[#030B2E]">
        <div className="absolute inset-0">
          {heroImages.map((image, index) => (
            <div
              key={image}
              className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-[1600ms] ease-out ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
              style={{ backgroundImage: `url(${image})` }}
            />
          ))}
        </div>
        <div className="absolute inset-0 bg-[#030B2E]/55"></div>
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(3,11,46,0.92)_0%,rgba(3,11,46,0.82)_42%,rgba(3,11,46,0.64)_100%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(23,230,179,0.14),transparent_28%)]"></div>
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#030B2E] to-transparent"></div>
        <div className="container-custom relative z-10">
          <div className="max-w-4xl py-24 md:py-32">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
             
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
                Bem-vindo ao
                <span className="block text-gradient bg-gradient-to-r from-secondary-500 to-secondary-300 bg-clip-text text-transparent">
                  MEVAM Brasília
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-200/90 mb-8 max-w-2xl leading-relaxed">
                <strong>Ministério Evangelístico Vinde Amados Meus</strong> - Uma igreja que ama, 
                serve e edifica vidas no coração do Brasil.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/sobre" className="btn-secondary inline-flex items-center justify-center">
                  Conheça Nossa História
                  <ArrowRight className="ml-2" size={20} />
                </Link>
                <Link to="/contato" className="btn-secondary inline-flex items-center justify-center">
                  Venha nos Visitar
                  <MapPin className="ml-2" size={20} />
                </Link>
              </div>
              <div className="mt-10 flex items-center gap-3">
                {heroImages.map((image, index) => (
                  <button
                    key={`${image}-indicator`}
                    type="button"
                    onClick={() => setCurrentSlide(index)}
                    className={`h-1.5 transition-all duration-300 ${
                      index === currentSlide ? 'w-12 bg-[#17E6B3]' : 'w-6 bg-white/35 hover:bg-white/55'
                    }`}
                    aria-label={`Exibir imagem ${index + 1} do carrossel`}
                  />
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Palavras - Vídeos do YouTube */}
      <section className="section-padding bg-[#030B2E]">
        <div className="container-custom">
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Palavras
              </h2>
              <p className="text-[#B8C4E3] text-lg max-w-2xl mx-auto">
                Mensagens transformadoras que edificam, inspiram e renovam sua fé
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {loading ? (
              // Skeleton loading
              [1, 2, 3].map((i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="group"
                >
                  <div className="relative overflow-hidden rounded-2xl bg-[#030B2E] border border-white/10">
                    <div className="relative aspect-video bg-gradient-to-br from-[#030B2E] via-[#17E6B3]/10 to-[#030B2E] animate-pulse">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-16 h-16 rounded-full bg-white/10 backdrop-blur-sm"></div>
                      </div>
                    </div>
                    <div className="p-5">
                      <div className="h-6 bg-white/10 rounded animate-pulse"></div>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              videos.map((video, index) => (
                <motion.div
                  key={video.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="group cursor-pointer"
                  onClick={() => setSelectedVideo(video)}
                >
                  <div className="relative overflow-hidden rounded-2xl bg-[#030B2E] border border-white/10 hover:border-[#17E6B3]/50 transition-all duration-300 group-hover:shadow-2xl group-hover:shadow-[#17E6B3]/20">
                    <div className="relative aspect-video overflow-hidden">
                      {/* Thumbnail real do YouTube */}
                      <img 
                        src={video.thumbnail}
                        alt={video.titulo}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        onError={(e) => {
                          // Fallback para thumbnail de qualidade média se maxresdefault falhar
                          e.target.src = `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`;
                        }}
                      />
                      
                      {/* Overlay com botão play */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20 flex items-center justify-center opacity-100 group-hover:opacity-100 transition-opacity">
                        <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:bg-[#17E6B3]/80 group-hover:scale-110 transition-all duration-300">
                          <Play className="text-white ml-1" size={28} fill="white" />
                        </div>
                      </div>
                      
                      {/* Badge da categoria */}
                      <div className="absolute top-3 left-3">
                        <span className="px-3 py-1 bg-[#17E6B3]/90 backdrop-blur-sm text-white text-xs font-semibold rounded-full">
                          {video.categoria}
                        </span>
                      </div>
                      
                      {/* Data */}
                      <div className="absolute bottom-3 right-3">
                        <span className="text-white text-xs bg-black/50 backdrop-blur-sm px-2 py-1 rounded-lg">
                          {video.data}
                        </span>
                      </div>
                    </div>
                    
                    {/* Título */}
                    <div className="p-5">
                      <h3 className="text-white font-bold text-lg line-clamp-2 group-hover:text-[#17E6B3] transition-colors">
                        {video.titulo}
                      </h3>
                      {video.pregador && (
                        <p className="text-[#B8C4E3] text-sm mt-2">
                          {video.pregador}
                        </p>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>

          <div className="text-center">
            <Link 
              to="/palavras" 
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#17E6B3] hover:bg-[#17E6B3]/90 text-[#030B2E] font-semibold rounded-xl hover:shadow-xl hover:shadow-[#17E6B3]/30 transition-all duration-300"
            >
              Ver todas as palavras
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Sobre Nós */}
      <section className="section-padding bg-[#030B2E]">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Quem Somos
              </h2>
              <p className="text-[#B8C4E3] text-lg mb-6">
                Somos uma igreja que acredita em um evangelho que se aproxima, pensamos em um 
                evangelho que não desiste das pessoas. Uma igreja onde o pecado será tratado 
                com rigor, mas o pecador será tratado com amor.
              </p>
              <p className="text-[#B8C4E3] text-lg mb-6">
                Somos um lugar de encorajadores. Deus nos disse para segurar as mãos uns dos 
                outros e falar "bora", vamos chegar até o final juntos, ninguém irá ficar pra trás.
              </p>
              <div className="flex items-center space-x-4 mb-8">
                <div className="flex items-center space-x-2">
                  <Heart className="text-[#17E6B3]" size={20} />
                  <span className="font-semibold text-white">Grande Para Servir</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Users className="text-[#17E6B3]" size={20} />
                  <span className="font-semibold text-white">Pequena Para Se Importar</span>
                </div>
              </div>
              <Link to="/sobre" className="bg-[#17E6B3] hover:bg-[#17E6B3]/90 text-[#030B2E] font-semibold py-3 px-8 rounded-xl transition-all inline-flex items-center shadow-lg shadow-[#17E6B3]/30">
                Saiba Mais
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-[#17E6B3]/20 to-[#030B2E] rounded-2xl p-8 text-white border border-[#17E6B3]/30 shadow-xl shadow-[#17E6B3]/10">
                <h3 className="text-2xl font-bold mb-4">Nossa Missão</h3>
                <p className="text-lg mb-6 text-[#B8C4E3]">
                  Levar cada pessoa a um relacionamento público e crescente com Jesus, 
                  impactando Brasília com o amor de Deus.
                </p>
                <div className="bg-white/10 rounded-lg p-4 border border-[#17E6B3]/20">
                  <h4 className="font-semibold mb-2 text-[#17E6B3]">Nossos Valores:</h4>
                  <ul className="space-y-1 text-sm text-[#B8C4E3]">
                    <li>• Ler a Bíblia</li>
                    <li>• Orar</li>
                    <li>• Jejuar</li>
                    <li>• Amar</li>
                    <li>• Servir</li>
                    <li>• Edificar</li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Eventos em Destaque */}
      <section className="section-padding bg-[#030B2E]">
        <div className="container-custom">
          <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-end">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-xl"
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold text-[#9CF7DF] backdrop-blur-sm">
                <CalendarRange size={16} />
                Eventos e inscrições
              </span>
              <h2 className="mt-5 text-3xl font-bold text-white md:text-4xl lg:text-5xl">
                Próximos Eventos
              </h2>
              <p className="mt-4 text-base leading-7 text-[#B8C4E3] md:text-lg">
                Confira os eventos com inscrição aberta e participe das programações da MEVAM Brasília.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="hidden gap-4 md:grid md:grid-cols-3"
            >
              <div className="rounded-2xl border border-white/10 bg-white/5 px-5 py-4 backdrop-blur-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9CF7DF]">Programação</p>
                <p className="mt-2 text-sm text-[#D9E4FF]">Veja os eventos disponíveis com datas e horários.</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 px-5 py-4 backdrop-blur-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9CF7DF]">Inscrição</p>
                <p className="mt-2 text-sm text-[#D9E4FF]">Acesse o evento e faça sua inscrição de forma simples.</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 px-5 py-4 backdrop-blur-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9CF7DF]">Novidades</p>
                <p className="mt-2 text-sm text-[#D9E4FF]">Fique por dentro das próximas programações especiais.</p>
              </div>
            </motion.div>
          </div>
          {eventsError && (
            <div className="mb-8 rounded-2xl border border-amber-400/30 bg-amber-300/10 px-5 py-4 text-sm text-amber-100">
              {eventsError}
            </div>
          )}

          {loadingEvents ? (
            <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((item) => (
                <div key={item} className="overflow-hidden rounded-[1.5rem] border border-white/10 bg-[#0d163f] shadow-[0_20px_60px_rgba(0,0,0,0.22)]">
                  <div className="aspect-[16/10] animate-pulse bg-white/10" />
                  <div className="space-y-3 p-5">
                    <div className="h-4 animate-pulse rounded bg-white/10" />
                    <div className="h-8 animate-pulse rounded bg-white/10" />
                    <div className="h-20 animate-pulse rounded bg-white/10" />
                  </div>
                </div>
              ))}
            </div>
          ) : publicEvents.length ? (
            <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {publicEvents.map((event, index) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <EventCard event={event} compact />
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="mt-12">
              <HomeEventsEmptyState />
            </div>
          )}
          <div className="text-center mt-12">
            <Link to="/eventos" className="inline-flex items-center justify-center rounded-2xl border border-[#17E6B3]/30 bg-[#143758] px-8 py-3.5 font-semibold text-white transition-colors hover:bg-[#17E6B3] hover:text-[#030B2E]">
              Ver todos os eventos
            </Link>
          </div>
        </div>
      </section>

      {/* Eventos/Agenda */}
      <section className="section-padding bg-[#030B2E]">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Nossa Programação
            </h2>
            <p className="text-[#B8C4E3] text-lg max-w-2xl mx-auto">
              Participe dos nossos cultos e atividades semanais. Toda a família é bem-vinda!
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {eventos.map((evento, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white/5 backdrop-blur-sm border border-[#17E6B3]/20 rounded-xl p-6"
              >
                <div className="flex items-center mb-4">
                  <Calendar className="text-[#17E6B3] mr-3" size={24} />
                  <div>
                    <h3 className="text-xl font-semibold text-white">
                      {evento.titulo}
                    </h3>
                    <p className="text-[#17E6B3] font-medium">
                      {evento.data} às {evento.horario}
                    </p>
                  </div>
                </div>
                <p className="text-[#B8C4E3]">{evento.descricao}</p>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/agenda" className="bg-[#17E6B3] hover:bg-[#17E6B3]/90 text-[#030B2E] font-semibold py-3 px-8 rounded-xl transition-all inline-flex items-center justify-center shadow-lg shadow-[#17E6B3]/30">
              Ver Agenda Completa
            </Link>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="section-padding bg-gradient-to-br from-[#030B2E] to-[#17E6B3]/20 text-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Venha Fazer Parte da Nossa Família
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto text-[#B8C4E3]">
              Você é muito bem-vindo! Junte-se a nós em nossa jornada de fé, 
              crescimento e serviço ao Reino de Deus.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contato" className="bg-[#17E6B3] hover:bg-[#17E6B3]/90 text-[#030B2E] font-semibold py-3 px-8 rounded-xl transition-all inline-flex items-center justify-center">
                <MapPin className="mr-2" size={20} />
                Como Chegar
              </Link>
              <a href="tel:61 99818-7484" className="border-2 border-white text-white hover:bg-white hover:text-[#030B2E] font-semibold py-3 px-8 rounded-xl transition-all inline-flex items-center justify-center">
                <Phone className="mr-2" size={20} />
                (61) 99818-7484
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Modal de vídeo */}
      <VideoModal 
        isOpen={!!selectedVideo}
        onClose={() => setSelectedVideo(null)}
        videoId={selectedVideo?.id}
        title={selectedVideo?.titulo}
      />
    </div>
  );
};

export default Home;